import {Input} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import {Text} from 'src/design/atoms/Text';
import React, {useCallback, useEffect, useRef} from 'react';
import DigitalSignature from 'react-digital-signature';
import {Button} from 'src/design/atoms/Button';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface FormSignatureV2Props {
  field: IFormFieldV2;
  value: string;
  error?: string;
  onChange: (name: string, value: string) => void;
}

export const FormSignatureV2 = React.memo<FormSignatureV2Props>(
  ({field, value, error, onChange}: FormSignatureV2Props) => {
    const refDigitalPad = useRef<any>(null);

    useEffect(() => {
      if (value) {
        refDigitalPad.current.fromDataURL(value);
      }
    }, []);

    useEffect(() => {
      if (refDigitalPad) {
        refDigitalPad.current.addEventListener('afterUpdateStroke', () => {
          const data = refDigitalPad.current.toDataURL();
          onChange(field.name, String(data));
        });
      }
    }, [refDigitalPad]);

    const handleClear = useCallback(() => {
      if (refDigitalPad != undefined) {
        refDigitalPad?.current?.clear();
        onChange(field.name, '');
      }
    }, [refDigitalPad, field.name, onChange]);

    return (
      <Box data-testid={`form-field-${field.name}`}>
        <Input.Wrapper
          label={field.label}
          withAsterisk={field.required}
          error={error}
          description={field.description}>
          <Space mb="xs" />
          {field.disabled ? (
            <Text c="dimmed" size="sm">
              Signature Disabled
            </Text>
          ) : (
            <DigitalSignature
              signaturePadRef={refDigitalPad}
              eraseOnResize={false}
              canvasProps={{
                style: {
                  background: 'white',
                  border: '1px solid',
                  borderColor: error ? 'rgba(255,0,0,.5)' : 'rgba(0,0,0,.3)',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  width: '100%',
                  maxWidth: '500px',
                  height: '200px',
                },
              }}
            />
          )}
        </Input.Wrapper>
        <Space mb="xs" />
        <Button aria-label="save" onClick={handleClear} size="sm">
          Clear
        </Button>
      </Box>
    );
  }
);
